import { Avatar, Badge, Button, Card, CardContent, CardHeader, CardTitle, Progress } from '@bs/ui-components'
import { BriefcaseBusiness, Code2, FileText, Globe2, GraduationCap, Languages, Loader2, MapPin, MessageCircle, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { DialogField, EditDialog } from '@/components/ui/edit-dialog'
import { completionFromChecklist, computeRealChecklist, ProfileAside } from '@/features/profile/components/profile-aside'
import { SearchPicker, type SearchPickerOption } from '@/components/ui/search-picker'
import { JobPreferencesEditor, type JobPreferencesOptions, type JobPreferencesSection } from './job-preferences-editor'
import { ProfileSkillsEditor } from './profile-skills-editor'
import { availabilityOptions, type AvailabilityId, type EducationView, type ExperienceView, type JobPreferencesView, type ProfileSkillView, type SocialLinksView, type ProfileChecklistId } from '@/features/profile/types'
import { updateMyAccount } from '@/services/account'
import { useAccount } from '@/app/use-account'
import { getMyProfile, updateMyProfile, type TalentProfile } from '@/features/profile/api'
import { AVAILABILITY_TO_STATUS, STATUS_TO_AVAILABILITY, getMyJobPreferences, updateMyJobPreferences, type JobPreferences as RealJobPreferences, type UpdateJobPreferencesInput } from '@/services/job-preferences'
import { formatLocation, getCities, getCompanies, getIndustries, getLookup, getRoles, getSkills, toSelectOptions, type CityItem, type CompanyItem, type MetadataItem } from '@/services/metadata'
import { createExclusion, getExclusions, removeExclusion, type Exclusion } from '@/features/profile/exclusions-api'
import { deleteResume, getResumeFile, updateResume, uploadResume } from '@/services/resumes'
import { deleteAvatar as deleteRealAvatar, uploadAvatar } from '@/features/profile/avatar-api'
import { companyLogoUrl } from '@/services/assets'

type ProfileTab = 'profile' | 'preferences' | 'resumes' | 'blocked'
type Editor = 'personal' | 'social' | 'about' | 'skills' | 'experience' | 'education' | 'languages' | 'blocked' | null

/** What `ProfilePage` computes once and passes down for display. */
interface ProfileView {
  name: string
  role: string
  location: string
  avatarDataUrl: string
  socialLinks: SocialLinksView
  about: string
  skills: ProfileSkillView[]
  experience: ExperienceView[]
  education: EducationView[]
  languages: string[]
}

/**
 * `bs-core.lookup_items` category `seniorityLevels` carries junk test rows alongside the real
 * ones (seeded once, never cleaned up — "sen1", "an test", "ad", "test 123", "test",
 * "Thi_Test_Junior", "Thi_Test_Mid level"), and the metadata API doesn't project a field that
 * would let a caller tell them apart (no `order`, no `status`). A label allowlist is the only
 * reliable filter available from this endpoint.
 */
const SENIORITY_ALLOWLIST = [
  'Intern', 'Junior', 'Experienced', 'Senior', 'Team Leader', 'Manager',
  'Director', 'CxO', 'Consultant', 'Owner', 'Founder', 'VP',
]

/**
 * `languages[].level` is NOT resolved against `bs-core.lookup_items` category `languageLevels` —
 * that category is a different, legacy employer-side vocabulary with only 2 stale rows
 * (`elId 1: "Good"`, `elId 2: "Mother tongue"`), never read by legacy's OWN talent-dashboard
 * Language editor. The real editor hardcodes this LinkedIn-standard 5-point scale client-side
 * (`constants.ts`'s `languageSpeakingLevels`) and writes its `elId` straight into `level` — so
 * this is the one field on this page that is correctly a static constant, not an API fetch.
 */
const EMPTY_PREFERENCES: JobPreferencesView = {
  workspaceTypes: [], seniorityLevels: [], jobTitles: [], skills: [], jobTypes: [],
  desiredSalary: '', currency: '', desiredLocations: [], companySizes: [], industries: [],
}

const LANGUAGE_LEVELS: MetadataItem[] = [
  { elId: 1, value: 'Elementary' },
  { elId: 2, value: 'Limited' },
  { elId: 3, value: 'Professional' },
  { elId: 4, value: 'Full Professional' },
  { elId: 5, value: 'Native or Bilingual' },
]

/**
 * Real employment has no per-entry id (it's a plain array on `bs-talent.profiles`), so the array
 * INDEX is the id — same trick `languages` already uses below for the same reason. Year-only in
 * the read view; the edit dialog reads the full ISO date separately
 * (see `openEditor`) so precision isn't lost just because the read view rounds to a year.
 */
function employmentToExperience(
  employment: TalentProfile['employment'],
  companyLogosById: Record<string, string>,
): ExperienceView[] {
  return employment.map((entry, index) => {
    const rawLogo = entry.companyLogoUrl ?? (entry.companyId ? companyLogosById[entry.companyId] : undefined)
    const logoUrl = companyLogoUrl(rawLogo)
    return {
      id: String(index),
      title: entry.titleName ?? '',
      company: entry.companyName ?? '',
      period: `${formatYear(entry.from)}–${entry.to ? formatYear(entry.to) : 'Present'}`,
      ...(entry.description ? { description: entry.description } : {}),
      ...(logoUrl ? { companyLogoUrl: logoUrl } : {}),
    }
  })
}

function formatYear(value: string | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : String(date.getFullYear())
}

/** `TalentProfile['employment'][number]` → the PATCH input shape (`companyName` required). */
function toEmploymentInput(entry: TalentProfile['employment'][number]) {
  return {
    companyName: entry.companyName ?? '',
    ...(entry.companyId !== undefined && { companyId: entry.companyId }),
    ...(entry.titleName !== undefined && { titleName: entry.titleName }),
    ...(entry.titleId !== undefined && { titleId: entry.titleId }),
    ...(entry.from !== undefined && { from: entry.from }),
    ...(entry.to !== undefined && { to: entry.to }),
    ...(entry.description !== undefined && { description: entry.description }),
  }
}

/**
 * `TalentProfile['education'][number]` → the PATCH input shape (`educationInstitution` required,
 * `degreeName`/`degreeType` string-only — the write contract, unlike the read side, never accepts
 * the bad numeric shape). Used to carry forward entries NOT being touched by the current edit;
 * `String()` here just preserves whatever a legacy-bug row already held, it doesn't resolve it —
 * the next read still runs it through `resolveMaybeElId` for display.
 */
function toEducationInput(entry: TalentProfile['education'][number]) {
  return {
    educationInstitution: entry.educationInstitution ?? '',
    ...(entry.educationInstitutionId !== undefined && { educationInstitutionId: entry.educationInstitutionId }),
    ...(entry.degreeNameId !== undefined && { degreeNameId: entry.degreeNameId }),
    ...(entry.degreeName !== undefined && { degreeName: String(entry.degreeName) }),
    ...(entry.degreeTypeId !== undefined && { degreeTypeId: entry.degreeTypeId }),
    ...(entry.degreeType !== undefined && { degreeType: String(entry.degreeType) }),
    ...(entry.to !== undefined && { to: entry.to }),
  }
}

/**
 * `education.degreeName`/`degreeType` are meant to hold the resolved LABEL (the schema pairs
 * each with its own `*Id` elId field) — but the migrated data has a real bug: it stuffed the raw
 * elId into the label field instead and left `degreeNameId`/`degreeTypeId` empty (verified
 * directly against a migrated record). Worse, `.lean()` skips Mongoose's schema casting, so that
 * bad value comes back as an actual JS `number`, not a numeric-looking string — the field is
 * typed `string` here because that's what the schema DECLARES, not what a pre-existing bad
 * document actually holds. Accepting `number` too (and never assuming `.trim()` exists) is what
 * keeps this a display rescue instead of a crash. A real label is never all-digits, so the
 * numeric-string branch can't misfire on genuinely-written data either.
 */
function resolveMaybeElId(value: string | number | undefined, lookup: MetadataItem[]): string {
  if (value === undefined || value === '') return ''
  if (typeof value === 'number') return lookup.find((item) => item.elId === value)?.value ?? String(value)
  if (!/^\d+$/.test(value.trim())) return value
  return lookup.find((item) => item.elId === Number(value))?.value ?? value
}

/**
 * `languages` stores ISO-639-1 codes ("en", "he") as its `value`, not display names — legacy
 * never denormalised a name for this one, unlike `degreeName`/`companyName`. `Intl.DisplayNames`
 * is a standard, built into every browser this app supports; no fetch, no hardcoded table to
 * keep in sync with the 197-row lookup category.
 */
const languageDisplayNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl ? new Intl.DisplayNames(['en'], { type: 'language' }) : null
function languageDisplayName(code: string): string {
  try {
    return languageDisplayNames?.of(code) ?? code
  } catch {
    return code
  }
}

export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const tab: ProfileTab = isProfileTab(requestedTab) ? requestedTab : 'profile'
  const [editor, setEditor] = useState<Editor>(null)
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [profileSkillsDraft, setProfileSkillsDraft] = useState<ProfileSkillView[]>([])
  const resumeInputRef = useRef<HTMLInputElement>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const [resumeError, setResumeError] = useState('')
  const [previewCvIndex, setPreviewCvIndex] = useState<number | null>(null)
  const { account, setAccount } = useAccount()
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [realProfile, setRealProfile] = useState<TalentProfile | null>(null)
  const [exclusions, setExclusions] = useState<Exclusion[]>([])
  /**
   * Legacy SuggestCompaniesToBlockWrapper: the companies on the talent's own employment history
   * that are not blocked yet — the obvious candidates for "don't show my profile to my employer".
   */
  const employmentBlockSuggestions = useMemo(() => {
    const blockedIds = new Set(exclusions.map((entry) => String(entry.excludedId)))
    const blockedNames = new Set(exclusions.map((entry) => (entry.excludedName ?? '').toLowerCase()))
    const seen = new Set<string>()
    return (realProfile?.employment ?? []).flatMap((entry) => {
      const name = entry.companyName?.trim()
      if (!name) return []
      const key = (entry.companyId ?? name).toLowerCase()
      if (seen.has(key) || (entry.companyId && blockedIds.has(entry.companyId)) || blockedNames.has(name.toLowerCase())) return []
      seen.add(key)
      return [{ name, ...(entry.companyId ? { companyId: entry.companyId } : {}), ...(entry.companyLogoUrl ? { logoUrl: entry.companyLogoUrl } : {}) }]
    })
  }, [exclusions, realProfile?.employment])

  async function handleBlockSuggested(suggestion: { name: string; companyId?: string; logoUrl?: string }) {
    try {
      let companyId = suggestion.companyId
      if (!companyId) {
        const found = await getCompanies({ q: suggestion.name })
        companyId = found.find((company) => company.name.toLowerCase() === suggestion.name.toLowerCase())?._id ?? found[0]?._id
      }
      if (!companyId) return
      const { exclusion } = await createExclusion({
        excludedType: 'company',
        excludedId: companyId,
        excludedName: suggestion.name,
        ...(suggestion.logoUrl && { excludedLogoUrl: suggestion.logoUrl }),
      })
      setExclusions((current) => [exclusion, ...current])
    } catch {
      // The chip stays; the manual "Add company" path is the fallback.
    }
  }
  const [loadError, setLoadError] = useState('')
  const [formError, setFormError] = useState('')
  const [personalCity, setPersonalCity] = useState<SearchPickerOption | null>(null)
  const [companyLogosById, setCompanyLogosById] = useState<Record<string, string>>({})
  const [personalCityDraft, setPersonalCityDraft] = useState<SearchPickerOption | null>(null)
  const [profileMeta, setProfileMeta] = useState<{
    degreeName: MetadataItem[]
    degreeType: MetadataItem[]
    language: MetadataItem[]
    role: MetadataItem[]
    skill: MetadataItem[]
  } | null>(null)
  // Both loaded, or neither counted — completion over a half-loaded record would swing as each
  // fetch lands, reading as data churn rather than loading.
  // Same function the aside uses, so the header and the checklist cannot disagree.
  const completion = completionFromChecklist(
    account && realProfile ? computeRealChecklist(account, realProfile) : [],
  )
  const tabs: Array<{ value: ProfileTab; label: string }> = [
    { value: 'profile', label: 'Profile' },
    { value: 'preferences', label: 'Job Preferences' },
    { value: 'resumes', label: 'Resumes' },
    { value: 'blocked', label: 'Blocked Companies' },
  ]

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getMyProfile(),
      getLookup('degreeNames'),
      getLookup('degreeTypes'),
      getLookup('languages'),
      getRoles(),
      getSkills(),
      getExclusions(),
    ])
      .then(([profileRes, degreeName, degreeType, language, role, skill, exclusionsRes]) => {
        if (cancelled) return
        setRealProfile(profileRes.profile)
        setProfileMeta({ degreeName, degreeType, language, role, skill })
        setExclusions(exclusionsRes.items)
      })
      .catch(() => {
        // Surfaced, not swallowed — with no fallback data left, a failed load renders an
        // all-empty profile indistinguishable from a brand-new one.
        if (!cancelled) setLoadError('Could not load your profile — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // `account` arrives from the shared provider, independently of the profile fetch above — the
  // one city this record has (if any), never the whole 722-row table, same "ids=" hydration path
  // Job Preferences uses for its (multi-)selected cities.
  useEffect(() => {
    const cityId = account?.location?.cityId
    if (cityId === undefined) return
    let cancelled = false
    getCities({ ids: [cityId] })
      .then((cities) => {
        if (!cancelled && cities[0]) setPersonalCity({ id: String(cities[0].cityId), label: cities[0].city })
      })
      .catch(() => {
        // Best-effort label hydration — the id itself already came from `account`.
      })
    return () => {
      cancelled = true
    }
  }, [account?.location?.cityId])

  /**
   * Migrated employment/education rows never got their own logo snapshot (only entries added
   * through THIS rebuild's company/school picker set one) — but every row keeps a `companyId` /
   * `educationInstitutionId`, which is enough to resolve the logo the same "ids=" way
   * personalCity/job-preferences cities do. Institutions resolve through the SAME `getCompanies`
   * call as employers — legacy's own "School" field reuses its company search verbatim (same
   * underlying entities), not a separate institutions catalog.
   */
  const employmentCompanyIds = [
    ...(realProfile?.employment ?? []).map((entry) => entry.companyId),
    ...(realProfile?.education ?? []).map((entry) => entry.educationInstitutionId),
  ].filter((id): id is string => Boolean(id) && !(id! in companyLogosById))
  const employmentCompanyIdsKey = employmentCompanyIds.join(',')
  useEffect(() => {
    if (!employmentCompanyIdsKey) return
    let cancelled = false
    getCompanies({ ids: employmentCompanyIdsKey.split(',') })
      .then((companies) => {
        if (cancelled) return
        const next: Record<string, string> = {}
        for (const company of companies) {
          if (company.logoUrl) next[company._id] = company.logoUrl
        }
        if (Object.keys(next).length) setCompanyLogosById((current) => ({ ...current, ...next }))
      })
      .catch(() => {
        // Best-effort — the entry still renders with its generic icon.
      })
    return () => {
      cancelled = true
    }
  }, [employmentCompanyIdsKey])

  /**
   * The candidate's own record, and nothing else.
   *
   * This used to be `hasReal ? real : mockProfile` on every field, so a failed call replaced the
   * signed-in person with a fixture — measured: with `/v1/me` answering 500, rami rahim's profile
   * rendered as "Maya Cohen, Senior Product Designer, Tel Aviv, Profile strength 100%". A candidate
   * has no way to tell that apart from their own data, which is what made it worse than an error.
   *
   * Empty fields are empty now. A profile with no `aboutMe` shows no About Me, which is true and
   * also the thing the completion checklist is there to prompt.
   */
  const view: ProfileView = {
    name: [account?.name.first, account?.name.last].filter(Boolean).join(' '),
    role: account?.title ?? '',
    location: formatLocation(personalCity?.label ? { city: personalCity.label } : undefined, account?.location?.country),
    avatarDataUrl: account?.avatarUrl ?? '',
    socialLinks: {
      linkedin: realProfile?.links?.linkedin ?? '',
      facebook: realProfile?.links?.facebook ?? '',
      twitter: realProfile?.links?.twitter ?? '',
      github: realProfile?.links?.github ?? '',
      website: realProfile?.links?.website ?? '',
    },
    about: realProfile?.aboutMe ?? '',
    skills: (realProfile?.skills ?? []).map((s) => ({ skillId: s.skillId ?? null, skillName: s.skillName ?? '' })),
    experience: employmentToExperience(realProfile?.employment ?? [], companyLogosById),
    education: (realProfile?.education ?? []).map((entry, index) => {
      const logoUrl = companyLogoUrl(entry.educationInstitutionId ? companyLogosById[entry.educationInstitutionId] : undefined)
      return {
        id: String(index),
        degree: [
          resolveMaybeElId(entry.degreeType, profileMeta?.degreeType ?? []),
          resolveMaybeElId(entry.degreeName, profileMeta?.degreeName ?? []),
        ].filter(Boolean).join(' · '),
        institution: entry.educationInstitution ?? '',
        period: entry.to ? String(new Date(entry.to).getFullYear()) : '',
        ...(logoUrl ? { institutionLogoUrl: logoUrl } : {}),
      }
    }),
    languages: (realProfile?.languages ?? []).map((entry) => {
      const code = (profileMeta?.language ?? []).find((l) => l.elId === entry.language)?.value
      const languageLabel = code ? languageDisplayName(code) : entry.languageName ?? ''
      const levelLabel = LANGUAGE_LEVELS.find((l) => l.elId === entry.level)?.value ?? ''
      return [languageLabel, levelLabel].filter(Boolean).join(' · ')
    }),
  }

  function selectTab(nextTab: ProfileTab) {
    setSearchParams(nextTab === 'profile' ? {} : { tab: nextTab }, { replace: true })
  }

  function openEditor(next: Exclude<Editor, null>, recordId?: string) {
    const [firstName, ...lastNameParts] = view.name.trim().split(/\s+/)
    const experience = next === 'experience' ? view.experience.find((item) => item.id === recordId) : undefined
    const realIndex = recordId !== undefined ? Number(recordId) : -1
    const realEmployment = next === 'experience' && realIndex >= 0 ? realProfile?.employment[realIndex] : undefined
    const realEducation = next === 'education' && realIndex >= 0 ? realProfile?.education[realIndex] : undefined
    const education = next === 'education' ? view.education.find((item) => item.id === recordId) : undefined
    const realLanguage = next === 'languages' && realIndex >= 0 ? realProfile?.languages[realIndex] : undefined
    const languageIndex = next === 'languages' && recordId !== undefined ? Number(recordId) : -1
    const languageParts = languageIndex >= 0 ? (view.languages[languageIndex] ?? '').split(' · ') : []
    const values: Record<string, string> = {
      personal: '',
      social: '',
      about: view.about,
      blocked: '',
      experience: '',
      education: '',
    }
    setEditingRecordId(recordId ?? null)
    setFormError('')
    if (next === 'skills') setProfileSkillsDraft(view.skills)
    if (next === 'personal') setPersonalCityDraft(personalCity)
    setDraft(next === 'personal'
        ? { firstName: firstName ?? '', lastName: lastNameParts.join(' '), headline: view.role, location: view.location, country: account!.location?.country ?? ''}
        : next === 'social'
          ? { ...view.socialLinks }
        : next === 'experience'
          ? realEmployment
            ? {
                company: realEmployment.companyName ?? '',
                ...(realEmployment.companyId !== undefined && { companyId: realEmployment.companyId }),
                title: realEmployment.titleName ?? '',
                ...(realEmployment.titleId !== undefined && { titleId: String(realEmployment.titleId) }),
                startDate: realEmployment.from?.slice(0, 10) ?? '',
                endDate: realEmployment.to?.slice(0, 10) ?? '',
                current: String(!realEmployment.to),
                description: realEmployment.description ?? '',
              }
            : { company: experience?.company ?? '', title: experience?.title ?? '', startDate: experience?.period.split('–')[0] ?? '', endDate: experience?.period.split('–')[1] === 'Present' ? '' : experience?.period.split('–')[1] ?? '', current: String(experience?.period.endsWith('Present') ?? false), description: experience?.description ?? '' }
          : next === 'education'
            ? realEducation
              ? {
                  degreeTypeId: realEducation.degreeTypeId !== undefined ? String(realEducation.degreeTypeId) : '',
                  degreeType: resolveMaybeElId(realEducation.degreeType, profileMeta!.degreeType),
                  degreeNameId: realEducation.degreeNameId !== undefined ? String(realEducation.degreeNameId) : '',
                  degree: resolveMaybeElId(realEducation.degreeName, profileMeta!.degreeName),
                  institution: realEducation.educationInstitution ?? '',
                  graduationDate: realEducation.to?.slice(0, 10) ?? '',
                }
              : { degreeType: education?.degree.split(' · ')[0] ?? '', degree: education?.degree.split(' · ')[1] ?? '', institution: education?.institution ?? '', graduationDate: education?.period ?? '' }
            : next === 'languages'
              ? realLanguage
                ? {
                    languageId: String(realLanguage.language),
                    language: languageDisplayName(profileMeta!.language.find((l) => l.elId === realLanguage.language)?.value ?? ''),
                    levelId: String(realLanguage.level),
                    level: LANGUAGE_LEVELS.find((l) => l.elId === realLanguage.level)?.value ?? '',
                  }
                : { language: languageParts[0] ?? '', level: languageParts[1] ?? '' }
            : { value: values[next] ?? '' })
    setEditor(next)
  }

  async function saveRealProfile(patch: Parameters<typeof updateMyProfile>[0]) {
    try {
      const { profile: updated } = await updateMyProfile(patch)
      setRealProfile(updated)
      setEditingRecordId(null)
      setEditor(null)
    } catch {
      setFormError('Could not save — please try again.')
    }
  }

  async function handleRemoveExclusion(id: string) {
    await removeExclusion(id).catch(() => {})
    setExclusions((current) => current.filter((item) => item._id !== id))
  }

  async function handleSetPrimaryResume(index: number) {
    const { profile: updated } = await updateResume(index, { isSelected: true }).catch(() => ({ profile: null }))
    if (updated) setRealProfile(updated)
  }

  async function handleDeleteResume(index: number) {
    const { profile: updated } = await deleteResume(index).catch(() => ({ profile: null }))
    if (updated) setRealProfile(updated)
  }

  async function saveEditor() {
    if (!editor) return

    if (editor === 'personal') {
      {
        try {
          const { user } = await updateMyAccount({
            name: { first: draft.firstName ?? '', last: draft.lastName ?? '' },
            title: draft.headline ?? '',
            ...(personalCityDraft && {
              location: { cityId: Number(personalCityDraft.id), country: draft.country ?? '' },
            }),
          })
          setAccount(user)
          setPersonalCity(personalCityDraft)
          setEditingRecordId(null)
          setEditor(null)
        } catch {
          setFormError('Could not save — please try again.')
        }
        return
      }
    }
    if (editor === 'social') {
      const links = { linkedin: draft.linkedin?.trim() ?? '', facebook: draft.facebook?.trim() ?? '', twitter: draft.twitter?.trim() ?? '', github: draft.github?.trim() ?? '', website: draft.website?.trim() ?? '' }
      return void saveRealProfile({ links })
    }
    if (editor === 'about') {
      return void saveRealProfile({ aboutMe: draft.value?.trim() ?? '' })
    }
    if (editor === 'skills') {
      {
        return void saveRealProfile({
          skills: profileSkillsDraft
            .filter((s) => s.skillName.trim())
            .map((s) => ({ skillName: s.skillName.trim(), ...(s.skillId ? { skillId: s.skillId } : {}) })),
        })
      }
    }
    if (editor === 'languages' && draft.language?.trim() && draft.level?.trim()) {
      {
        const index = editingRecordId !== null ? Number(editingRecordId) : -1
        const nextEntry = { language: Number(draft.languageId), level: Number(draft.levelId) }
        const nextLanguages = index >= 0
          ? realProfile!.languages.map((entry, i) => (i === index ? nextEntry : entry))
          : [...realProfile!.languages, nextEntry]
        return void saveRealProfile({ languages: nextLanguages })
      }
    }
    if (editor === 'experience' && draft.title?.trim() && draft.company?.trim() && draft.startDate?.trim() && (draft.current === 'true' || draft.endDate?.trim())) {
      const description = draft.description?.trim()
      {
        const index = editingRecordId !== null ? Number(editingRecordId) : -1
        const nextEntry = {
          companyName: draft.company.trim(),
          ...(draft.companyId && { companyId: draft.companyId }),
          titleName: draft.title.trim(),
          ...(draft.titleId && { titleId: Number(draft.titleId) }),
          from: draft.startDate.trim(),
          ...(draft.current === 'true' ? {} : { to: (draft.endDate ?? '').trim() }),
          ...(description ? { description } : {}),
        }
        const nextEmployment = index >= 0
          ? realProfile!.employment.map((entry, i) => (i === index ? nextEntry : toEmploymentInput(entry)))
          : [...realProfile!.employment.map(toEmploymentInput), nextEntry]
        return void saveRealProfile({ employment: nextEmployment })
      }
    }
    if (editor === 'education' && draft.institution?.trim() && draft.graduationDate?.trim()) {
      {
        const index = editingRecordId !== null ? Number(editingRecordId) : -1
        const nextEntry = {
          educationInstitution: draft.institution.trim(),
          to: draft.graduationDate.trim(),
          ...(draft.institutionId && { educationInstitutionId: draft.institutionId }),
          ...(draft.degreeTypeId && { degreeTypeId: Number(draft.degreeTypeId), degreeType: draft.degreeType }),
          ...(draft.degreeNameId && { degreeNameId: Number(draft.degreeNameId), degreeName: draft.degree }),
        }
        const nextEducation = index >= 0
          ? realProfile!.education.map((entry, i) => (i === index ? nextEntry : toEducationInput(entry)))
          : [...realProfile!.education.map(toEducationInput), nextEntry]
        return void saveRealProfile({ education: nextEducation })
      }
    }
    if (editor === 'blocked' && draft.value?.trim()) {
      {
        if (!draft.companyId) {
          setFormError('Pick a company from the search results.')
          return
        }
        try {
          const { exclusion } = await createExclusion({
            excludedType: 'company',
            excludedId: draft.companyId,
            excludedName: draft.value.trim(),
            ...(draft.companyLogoUrl && { excludedLogoUrl: draft.companyLogoUrl }),
          })
          setExclusions((current) => [exclusion, ...current])
          setEditingRecordId(null)
          setEditor(null)
        } catch {
          setFormError('Could not save — please try again.')
        }
        return
      }
    }
    setEditingRecordId(null)
    setEditor(null)
  }

  async function addResume(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    {
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!['pdf', 'doc', 'docx'].includes(ext ?? '')) {
        setResumeError('Choose a PDF, DOC or DOCX file.')
        event.target.value = ''
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        setResumeError('File must be smaller than 10 MB.')
        event.target.value = ''
        return
      }
      try {
        const { profile: updated } = await uploadResume(file)
        setRealProfile(updated)
        setResumeError('')
      } catch {
        setResumeError('Could not upload — please try again.')
      }
      event.target.value = ''
      return
    }
    event.target.value = ''
  }

  async function updateAvatar(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setAvatarError('Choose a PNG, JPG or WebP image.')
      event.target.value = ''
      return
    }
    {
      if (file.size > 5 * 1024 * 1024) {
        setAvatarError('Image must be smaller than 5 MB.')
        event.target.value = ''
        return
      }
      setAvatarUploading(true)
      try {
        const { user } = await uploadAvatar(file)
        setAccount(user)
        setAvatarError('')
      } catch {
        setAvatarError('Could not upload — please try again.')
      } finally {
        setAvatarUploading(false)
      }
      event.target.value = ''
      return
    }
  }

  async function removeAvatar() {
    {
      setAvatarUploading(true)
      const { user } = await deleteRealAvatar().catch(() => ({ user: null })).finally(() => setAvatarUploading(false))
      if (user) setAccount(user)
      setAvatarError('')
      return
    }
    setAvatarError('')
  }

  function removeExperience(id: string) {
    {
      const index = Number(id)
      void saveRealProfile({ employment: realProfile!.employment.filter((_, i) => i !== index).map(toEmploymentInput) })
      return
    }
  }

  function removeEducation(id: string) {
    {
      const index = Number(id)
      void saveRealProfile({ education: realProfile!.education.filter((_, i) => i !== index).map(toEducationInput) })
      return
    }
  }

  function removeLanguage(index: number) {
    {
      void saveRealProfile({ languages: realProfile!.languages.filter((_, i) => i !== index) })
      return
    }
  }

  function openChecklistItem(id: ProfileChecklistId) {
    if (id === 'resume') {
      selectTab('resumes')
      return
    }
    if (id === 'education') openEditor('education')
    if (id === 'experience') openEditor('experience')
    if (id === 'skills') openEditor('skills')
    if (id === 'headline') openEditor('personal')
    if (id === 'summary') openEditor('about')
  }

  return (
    <main className="mx-auto max-w-[1460px] px-4 py-8 sm:px-6 md:py-12 lg:px-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <section className="min-w-0 flex-1">
          <Card className="overflow-hidden rounded-[20px]">
            <div className="flex flex-col items-center gap-5 border-b border-border bg-white p-6 sm:flex-row sm:p-8">
              <button type="button" onClick={() => setAvatarEditorOpen(true)} className="group relative rounded-full" aria-label="Edit profile picture">
                <Avatar name={view.name} {...(view.avatarDataUrl ? { src: view.avatarDataUrl } : {})} className="size-28 text-2xl ring-4 ring-[#f1f4ff]" />
                {avatarUploading ? (
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-primary/55">
                    <Loader2 className="size-6 animate-spin text-white" />
                  </span>
                ) : (
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-primary/55 text-sm font-semibold text-white opacity-0 transition group-hover:opacity-100"><Pencil className="mr-1.5 size-4" /> Edit</span>
                )}
              </button>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center gap-3 sm:justify-start">
                  <h1 className="text-2xl font-semibold text-[#404040] sm:text-3xl">{view.name}</h1>
                  <Button variant="ghost" size="icon" aria-label="Edit profile" onClick={() => openEditor('personal')}><Pencil className="size-4" /></Button>
                </div>
                <p className="mt-1 text-lg text-[#555]">{view.role}</p>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-muted-foreground sm:justify-start">
                  <MapPin className="size-4" /> {view.location}
                </p>
                <SocialLinks socialLinks={view.socialLinks} onEdit={() => openEditor('social')} />
              </div>
              <div className="w-full rounded-xl bg-[#f7f8fc] p-4 sm:w-48">
                <div className="flex justify-between text-sm"><span>Profile strength</span><strong className="text-accent">{completion}%</strong></div>
                <Progress value={completion} className="mt-2" />
              </div>
            </div>

            <div className="flex gap-7 overflow-x-auto border-b border-border bg-white px-6 sm:px-8">
              {tabs.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => selectTab(item.value)}
                  className={`relative h-16 shrink-0 text-sm transition ${tab === item.value ? 'font-semibold text-accent after:absolute after:inset-x-0 after:bottom-0 after:h-1 after:bg-accent' : 'text-[#555] hover:text-accent'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <CardContent className="space-y-5 bg-[#fbfbfc] p-5 sm:p-7">
              {loadError ? <p role="alert" className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">{loadError}</p> : null}
              {tab === 'profile' ? <ProfileOverview onEdit={openEditor} view={view} onRemoveExperience={removeExperience} onRemoveEducation={removeEducation} onRemoveLanguage={removeLanguage} /> : null}
              {tab === 'preferences' ? <Preferences /> : null}
              {tab === 'resumes' ? (
                <Resumes
                  onUpload={() => resumeInputRef.current?.click()}
                  cvs={realProfile?.cvs ?? []}
                  error={resumeError}
                  onSetPrimary={(index) => void handleSetPrimaryResume(index)}
                  onDelete={(index) => void handleDeleteResume(index)}
                  onPreview={setPreviewCvIndex}
                />
              ) : null}
              {tab === 'blocked' ? (
                <BlockedCompanies
                  onAdd={() => openEditor('blocked')}
                  exclusions={exclusions}
                  onRemoveExclusion={(id) => void handleRemoveExclusion(id)}
                  suggestions={employmentBlockSuggestions}
                  onBlockSuggested={(suggestion) => void handleBlockSuggested(suggestion)}
                />
              ) : null}
            </CardContent>
          </Card>
        </section>
        <ProfileAside onChecklistAction={openChecklistItem} realProfile={realProfile} />
      </div>
      <input ref={resumeInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={addResume} />
      <input ref={avatarInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={updateAvatar} />
      {avatarEditorOpen ? (
        <EditDialog
          title="Edit Profile Picture"
          submitLabel="Done"
          description="Changes are saved to your profile."
          onClose={() => setAvatarEditorOpen(false)}
          onSubmit={() => setAvatarEditorOpen(false)}
        >
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <Avatar name={view.name} {...(view.avatarDataUrl ? { src: view.avatarDataUrl } : {})} className="size-40 text-3xl ring-4 ring-[#f1f4ff]" />
              {avatarUploading ? (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-primary/55">
                  <Loader2 className="size-8 animate-spin text-white" />
                </div>
              ) : null}
            </div>
            <p className="mt-5 text-sm text-muted-foreground">PNG, JPG or WebP. Maximum 5 MB.</p>
            {avatarError ? <p role="alert" className="mt-3 text-sm text-destructive">{avatarError}</p> : null}
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button type="button" disabled={avatarUploading} onClick={() => avatarInputRef.current?.click()}>Upload image</Button>
              <Button type="button" variant="outline" disabled={avatarUploading || !view.avatarDataUrl} onClick={() => void removeAvatar()}>Remove image</Button>
            </div>
          </div>
        </EditDialog>
      ) : null}
      {editor ? (
        <EditDialog
          title={editorTitle(editor, editingRecordId !== null)}
          submitLabel={((editor === 'experience' || editor === 'education') && editingRecordId === null) || editor === 'blocked' ? 'Add' : 'Save changes'}
          description="Changes are saved to your profile."
          onClose={() => { setEditingRecordId(null); setEditor(null) }}
          onSubmit={saveEditor}
        >
          {editor === 'skills'
            ? <ProfileSkillsEditor
                value={profileSkillsDraft}
                onChange={setProfileSkillsDraft}
                suggestions={profileMeta ? profileMeta.skill.map((s) => ({ skillId: s._id ?? null, skillName: s.value })) : []}
              />
            : <EditorFields
                editor={editor}
                draft={draft}
                setDraft={setDraft}
                dateFieldType="date"
                degreeTypeOptions={profileMeta ? toSelectOptions(profileMeta.degreeType) : []}
                degreeNameOptions={profileMeta ? toSelectOptions(profileMeta.degreeName) : []}
                languageOptions={profileMeta ? profileMeta.language.map((l) => ({ id: String(l.elId), label: languageDisplayName(l.value) })) : []}
                languageLevelOptions={toSelectOptions(LANGUAGE_LEVELS)}
                roleOptions={profileMeta ? toSelectOptions(profileMeta.role) : []}
                onLocationSearch={(q) => getCities({ q }).then((cities) => cities.map((c) => ({ id: String(c.cityId), label: c.city, data: c })))}
                onLocationPick={(option) => {
                  setPersonalCityDraft(option)
                  const city = option.data as { country?: string } | undefined
                  setDraft((current) => ({ ...current, country: city?.country ?? '' }))
                }}
                onCompanySearch={(q) => getCompanies({ q }).then((companies) => companies.map((c) => {
                  const logoUrl = companyLogoUrl(c.logoUrl)
                  return { id: c._id, label: c.name, data: c, ...(logoUrl ? { logoUrl } : {}) }
                }))}
              />}
          {formError ? <p role="alert" className="mt-4 text-sm text-destructive">{formError}</p> : null}
        </EditDialog>
      ) : null}
      {previewCvIndex !== null && realProfile ? (
        <ResumePreviewModal
          cv={realProfile!.cvs[previewCvIndex]!}
          index={previewCvIndex}
          onClose={() => setPreviewCvIndex(null)}
        />
      ) : null}
    </main>
  )
}

function isProfileTab(value: string | null): value is ProfileTab {
  return value === 'profile' || value === 'preferences' || value === 'resumes' || value === 'blocked'
}

function ProfileOverview({ onEdit, view, onRemoveExperience, onRemoveEducation, onRemoveLanguage }: { onEdit(editor: Exclude<Editor, null>, recordId?: string): void; view: ProfileView; onRemoveExperience(id: string): void; onRemoveEducation(id: string): void; onRemoveLanguage(index: number): void }) {
  return (
    <>
      <Section title="About me" action="Edit" onAction={() => onEdit('about')}>
        <p className="max-w-3xl leading-7 text-[#555]">{view.about || 'Add a short introduction about yourself.'}</p>
      </Section>
      <Section title="Skills" action="Edit" onAction={() => onEdit('skills')}>
        <div className="flex flex-wrap gap-2">{view.skills.map((skill) => <Badge key={`${skill.skillId ?? 'custom'}-${skill.skillName}`} variant="outline">{skill.skillName}</Badge>)}</div>
      </Section>
      <Section title="Experience" action="Add experience" onAction={() => onEdit('experience')}>
        {view.experience.map((item) => {
          const detail = [item.location, item.description].filter(Boolean).join(' · ')
          return <TimelineItem key={item.id} icon={BriefcaseBusiness} {...(item.companyLogoUrl ? { logoUrl: item.companyLogoUrl } : {})} title={item.title} subtitle={`${item.company} · ${item.period}`} {...(detail ? { detail } : {})} onEdit={() => onEdit('experience', item.id)} onRemove={() => onRemoveExperience(item.id)} />
        })}
      </Section>
      <Section title="Education" action="Add education" onAction={() => onEdit('education')}>
        {view.education.map((item) => <TimelineItem key={item.id} icon={GraduationCap} {...(item.institutionLogoUrl ? { logoUrl: item.institutionLogoUrl } : {})} title={item.degree} subtitle={`${item.institution} · ${item.period}`} onEdit={() => onEdit('education', item.id)} onRemove={() => onRemoveEducation(item.id)} />)}
      </Section>
      <Section title="Languages" action="Add language" onAction={() => onEdit('languages')}>
        <div className="grid gap-3 sm:grid-cols-2">{view.languages.map((language, index) => <div key={`${language}-${index}`} className="flex items-center gap-2 rounded-xl bg-[#fafafa] px-3 py-2"><Languages className="size-4 text-accent" /><span className="min-w-0 flex-1">{language}</span><Button type="button" size="icon" variant="ghost" aria-label={`Edit ${language}`} onClick={() => onEdit('languages', String(index))}><Pencil className="size-4" /></Button><Button type="button" size="icon" variant="ghost" aria-label={`Remove ${language}`} onClick={() => onRemoveLanguage(index)}><Trash2 className="size-4" /></Button></div>)}</div>
      </Section>
    </>
  )
}

const socialLinkMeta = [
  { key: 'linkedin', label: 'LinkedIn', icon: BriefcaseBusiness },
  { key: 'facebook', label: 'Facebook', icon: Users },
  { key: 'twitter', label: 'Twitter', icon: MessageCircle },
  { key: 'github', label: 'GitHub', icon: Code2 },
  { key: 'website', label: 'Personal website', icon: Globe2 },
] as const

function SocialLinks({ onEdit, socialLinks }: { onEdit(): void; socialLinks: ProfileView['socialLinks'] }) {
  const visibleLinks = socialLinkMeta.filter((item) => socialLinks[item.key])

  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
      {visibleLinks.map(({ key, label, icon: Icon }) => (
        <a key={key} href={socialLinks[key]} target="_blank" rel="noreferrer" aria-label={label} title={label} className="flex size-9 items-center justify-center rounded-full border border-border bg-white text-primary transition hover:border-accent hover:text-accent">
          <Icon className="size-4" />
        </a>
      ))}
      {visibleLinks.length === 0 ? <span className="text-sm text-muted-foreground">Add your social links</span> : null}
      <Button type="button" size="sm" variant="ghost" onClick={onEdit}><Pencil className="size-3.5" /> Edit Social Links</Button>
    </div>
  )
}

function Preferences() {
  const [editing, setEditing] = useState<JobPreferencesSection | null>(null)
  /**
   * The editor's working copy, seeded from the loaded preferences.
   *
   * `EMPTY_PREFERENCES` rather than a fixture: opening the editor before the real values arrive
   * shows blank fields, and blank is true. It used to seed from the mock provider, so a candidate
   * could open "Job Preferences" and be shown — and then SAVE — someone else's answers.
   */
  const [draft, setDraft] = useState<JobPreferencesView>(EMPTY_PREFERENCES)
  // Defaults to 2 (Actively looking) until the real preferences load — the product default,
  // same value `STATUS_TO_AVAILABILITY` maps a brand-new profile to.
  const [availabilityDraft, setAvailabilityDraft] = useState<AvailabilityId>(2)
  const [formError, setFormError] = useState('')
  const [loadError, setLoadError] = useState('')

  const [realPrefs, setRealPrefs] = useState<RealJobPreferences | null>(null)
  const [meta, setMeta] = useState<{
    workspaceType: MetadataItem[]
    seniorityLevel: MetadataItem[]
    jobType: MetadataItem[]
    companySize: MetadataItem[]
    currency: MetadataItem[]
    role: MetadataItem[]
    skill: MetadataItem[]
    industry: MetadataItem[]
    city: CityItem[]
  } | null>(null)


  useEffect(() => {
    let cancelled = false
    Promise.all([
      getMyJobPreferences(),
      getLookup('workspaceTypes'),
      getLookup('seniorityLevels'),
      getLookup('positionTypes'),
      getLookup('companySizes'),
      getLookup('currency'),
      getRoles(),
      getSkills(),
      getIndustries(),
    ])
      .then(async ([prefsRes, workspaceType, seniorityLevelRaw, jobType, companySize, currency, role, skill, industry]) => {
        if (cancelled) return
        // Cities are never preloaded whole (722 rows, no search term = nothing to narrow by —
        // see `LocationAutocomplete`) — hydrate only the ones this record already has selected,
        // by id, the same "ids=" path the search box's own results feed into below.
        const cityIds = prefsRes.preferences.desiredCityIds
        const city = cityIds.length ? await getCities({ ids: cityIds }).catch(() => []) : []
        if (cancelled) return
        setRealPrefs(prefsRes.preferences)
        setMeta({
          workspaceType,
          seniorityLevel: seniorityLevelRaw.filter((item) => SENIORITY_ALLOWLIST.includes(item.value)),
          jobType,
          companySize,
          currency,
          role,
          skill,
          industry,
          city,
        })
      })
      .catch(() => {
        // Surfaced, not swallowed — without preferences and their vocabularies this tab shows
        // blank sections and `save()` refuses to write, so it must not look merely empty.
        if (!cancelled) setLoadError('Could not load your job preferences — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  /** Grows as the location `SearchPicker` searches — every result it ever showed stays resolvable. */
  function mergeCities(options: SearchPickerOption[]) {
    setMeta((current) => {
      if (!current) return current
      const known = new Map(current.city.map((c) => [c.cityId, c]))
      for (const option of options) known.set(Number(option.id), { cityId: Number(option.id), city: option.label })
      return { ...current, city: Array.from(known.values()) }
    })
  }

  const idToLabel = (items: MetadataItem[], id: number) => items.find((item) => item.elId === id)?.value
  const labelToId = (items: MetadataItem[], label: string) => items.find((item) => item.value === label)?.elId
  const cityIdToLabel = (id: number) => meta?.city.find((c) => c.cityId === id)?.city
  const cityLabelToId = (label: string) => meta?.city.find((c) => c.city === label)?.cityId

  const view: JobPreferencesView = realPrefs && meta
    ? {
        workspaceTypes: realPrefs!.workspaceType.flatMap((id) => {
          const label = idToLabel(meta!.workspaceType, id)
          return label ? [label] : []
        }),
        seniorityLevels: realPrefs!.seniorityLevel.flatMap((id) => {
          const label = idToLabel(meta!.seniorityLevel, id)
          return label ? [label] : []
        }),
        jobTitles: realPrefs!.desiredRoles.flatMap((id) => {
          const label = idToLabel(meta!.role, id)
          return label ? [label] : []
        }),
        skills: realPrefs!.skills.flatMap((id) => {
          const label = idToLabel(meta!.skill, id)
          return label ? [{ skillId: String(id), skillName: label }] : []
        }),
        jobTypes: realPrefs!.positionTypes.flatMap((id) => {
          const label = idToLabel(meta!.jobType, id)
          return label ? [label] : []
        }),
        desiredSalary: realPrefs!.desiredSalary !== undefined ? String(realPrefs!.desiredSalary) : '',
        currency: realPrefs!.currency !== undefined ? idToLabel(meta!.currency, realPrefs!.currency) ?? '' : '',
        desiredLocations: realPrefs!.desiredCityIds.flatMap((id) => {
          const label = cityIdToLabel(id)
          return label ? [label] : []
        }),
        companySizes: realPrefs!.companySizes.flatMap((id) => {
          const label = idToLabel(meta!.companySize, id)
          return label ? [label] : []
        }),
        industries: realPrefs!.industries.flatMap((id) => {
          const label = idToLabel(meta!.industry, id)
          return label ? [label] : []
        }),
      }
    : {
        // Nothing loaded yet — empty, never a fixture. See the note on the profile view above.
        workspaceTypes: [], seniorityLevels: [], jobTitles: [], skills: [], jobTypes: [],
        desiredSalary: '', currency: '', desiredLocations: [], companySizes: [], industries: [],
      }

  const viewAvailability: AvailabilityId = realPrefs ? STATUS_TO_AVAILABILITY[realPrefs.status] : 2
  const availabilityLabel = availabilityOptions.find((option) => option.id === viewAvailability)!.label

  const options: JobPreferencesOptions = meta
    ? {
        workspaceType: toSelectOptions(meta!.workspaceType),
        seniorityLevel: toSelectOptions(meta!.seniorityLevel),
        jobTitle: toSelectOptions(meta!.role),
        skill: toSelectOptions(meta!.skill),
        jobType: toSelectOptions(meta!.jobType),
        location: [], // unused in real mode — `LocationAutocomplete` searches live instead
        companySize: toSelectOptions(meta!.companySize),
        industry: toSelectOptions(meta!.industry),
        currency: toSelectOptions(meta!.currency),
      }
    : {
        // Vocabulary not loaded yet — empty pickers, never invented options. A fixture option's
        // value maps to no elId, so choosing one wrote garbage the server could not resolve.
        workspaceType: [], seniorityLevel: [], jobTitle: [], skill: [], jobType: [],
        location: [], companySize: [], industry: [], currency: [],
      }

  function edit(section: JobPreferencesSection) {
    setDraft(view)
    setAvailabilityDraft(viewAvailability)
    setFormError('')
    setEditing(section)
  }

  async function save() {
    // Nothing to save against until the preferences and their vocabularies have loaded — the ids
    // below are resolved through `meta`, so saving early would write an empty patch.
    if (!realPrefs || !meta) return

    const patch: UpdateJobPreferencesInput = {
      status: AVAILABILITY_TO_STATUS[availabilityDraft],
      workspaceType: draft.workspaceTypes.flatMap((label) => {
        const id = labelToId(meta!.workspaceType, label)
        return id !== undefined ? [id] : []
      }),
      seniorityLevel: draft.seniorityLevels.flatMap((label) => {
        const id = labelToId(meta!.seniorityLevel, label)
        return id !== undefined ? [id] : []
      }),
      desiredRoles: draft.jobTitles.flatMap((label) => {
        const id = labelToId(meta!.role, label)
        return id !== undefined ? [id] : []
      }),
      skills: draft.skills.flatMap((skill) => {
        const id = Number(skill.skillId)
        return Number.isFinite(id) ? [id] : []
      }),
      positionTypes: draft.jobTypes.flatMap((label) => {
        const id = labelToId(meta!.jobType, label)
        return id !== undefined ? [id] : []
      }),
      ...(draft.desiredSalary.trim() ? { desiredSalary: Number(draft.desiredSalary) } : {}),
      ...(() => {
        const currencyId = draft.currency ? labelToId(meta!.currency, draft.currency) : undefined
        return currencyId !== undefined ? { currency: currencyId } : {}
      })(),
      desiredCityIds: draft.desiredLocations.flatMap((label) => {
        const id = cityLabelToId(label)
        return id !== undefined ? [id] : []
      }),
      companySizes: draft.companySizes.flatMap((label) => {
        const id = labelToId(meta!.companySize, label)
        return id !== undefined ? [id] : []
      }),
      industries: draft.industries.flatMap((label) => {
        const id = labelToId(meta!.industry, label)
        return id !== undefined ? [id] : []
      }),
    }

    try {
      const { preferences } = await updateMyJobPreferences(patch)
      setRealPrefs(preferences)
      setEditing(null)
    } catch {
      setFormError('Could not save — please try again.')
    }
  }

  function cancel() {
    setDraft(view)
    setAvailabilityDraft(viewAvailability)
    setEditing(null)
  }

  return (
    <>
      {loadError ? <p role="alert" className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">{loadError}</p> : null}
      <PreferenceEditSection title="About You" editing={editing === 'about-you'} onEdit={() => edit('about-you')} onSave={save} onCancel={cancel}>
        {editing === 'about-you' ? <>
          <JobPreferencesEditor section="about-you" value={draft} onChange={setDraft} availability={availabilityDraft} onAvailabilityChange={setAvailabilityDraft} options={options} onLocationResultsSeen={mergeCities} />
          {formError ? <p role="alert" className="mt-4 text-sm text-destructive">{formError}</p> : null}
        </> : <div className="grid gap-5 sm:grid-cols-2">
        <PreferenceTags label="Availability" values={[availabilityLabel]} />
        <PreferenceTags label="Workspace Type" values={view.workspaceTypes} />
        <PreferenceTags label="Seniority Level" values={view.seniorityLevels} />
        <PreferenceTags label="Job Title(s)" values={view.jobTitles} />
        <PreferenceTags label="Skills" values={view.skills.map((skill) => skill.skillName)} />
        </div>}
      </PreferenceEditSection>

      <PreferenceEditSection title="About Your Job" editing={editing === 'about-job'} onEdit={() => edit('about-job')} onSave={save} onCancel={cancel}>
        {editing === 'about-job' ? <>
          <JobPreferencesEditor section="about-job" value={draft} onChange={setDraft} availability={availabilityDraft} onAvailabilityChange={setAvailabilityDraft} options={options} onLocationResultsSeen={mergeCities} />
          {formError ? <p role="alert" className="mt-4 text-sm text-destructive">{formError}</p> : null}
        </> : <div className="grid gap-5 sm:grid-cols-2">
        <PreferenceTags label="Job Type" values={view.jobTypes} />
        <Preference label="Desired Salary" value={`${view.desiredSalary || 'Not set'} ${view.currency}`} />
        <PreferenceTags label="Desired Location" values={view.desiredLocations} />
        </div>}
      </PreferenceEditSection>

      <PreferenceEditSection title="About Your Company" editing={editing === 'about-company'} onEdit={() => edit('about-company')} onSave={save} onCancel={cancel}>
        {editing === 'about-company' ? <>
          <JobPreferencesEditor section="about-company" value={draft} onChange={setDraft} availability={availabilityDraft} onAvailabilityChange={setAvailabilityDraft} options={options} onLocationResultsSeen={mergeCities} />
          {formError ? <p role="alert" className="mt-4 text-sm text-destructive">{formError}</p> : null}
        </> : <div className="grid gap-5 sm:grid-cols-2">
        <PreferenceTags label="Company Size" values={view.companySizes} />
        <PreferenceTags label="Industry" values={view.industries} />
        </div>}
      </PreferenceEditSection>
    </>
  )
}

function Resumes({
  onUpload,
  cvs,
  error,
  onSetPrimary,
  onDelete,
  onPreview,
}: {
  onUpload(): void
  cvs: TalentProfile['cvs']
  error: string
  onSetPrimary(index: number): void
  onDelete(index: number): void
  onPreview(index: number): void
}) {
  const [showSingleResumeWarning, setShowSingleResumeWarning] = useState(false)

  /**
   * A candidate cannot delete their LAST CV from here.
   *
   * Not a UI nicety: recruiters send the CV on file, so a profile with none silently drops out of
   * every send path. The warning says so instead of letting the delete succeed and the candidate
   * discover it later.
   */
  function removeRealResume(index: number) {
    if (cvs.length === 1) {
      setShowSingleResumeWarning(true)
      return
    }
    onDelete(index)
  }

  return (
    <>
      <Section title="Resumes" action="Upload resume" onAction={onUpload}>
        {cvs.map((cv, index) => (
              <div key={index} className="flex items-center gap-4 rounded-xl border border-border bg-[#fafafa] p-4">
                <button type="button" onClick={() => onPreview(index)} className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent" aria-label={`Preview ${cv.displayName ?? cv.fileName}`}><FileText /></button>
                <button type="button" onClick={() => onPreview(index)} className="min-w-0 flex-1 text-left">
                  <strong className="block truncate text-primary">{cv.displayName ?? cv.fileName}</strong>
                  <p className="mt-1 text-sm text-muted-foreground">{cv.uploadedAt ? `Uploaded ${new Date(cv.uploadedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}</p>
                </button>
                {cv.isSelected ? <Badge variant="success">Primary</Badge> : <Button size="sm" variant="ghost" onClick={() => onSetPrimary(index)}>Make primary</Button>}
                <Button size="icon" variant="ghost" aria-label="Remove resume" onClick={() => removeRealResume(index)}><Trash2 className="size-4" /></Button>
              </div>
            ))}
        {cvs.length === 0 ? <EmptyState text="You haven't uploaded any resumes yet." /> : null}
      </Section>
      {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}

      {showSingleResumeWarning ? (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-primary/45 p-4 backdrop-blur-[2px]" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowSingleResumeWarning(false) }}>
          <div role="dialog" aria-modal="true" aria-labelledby="single-resume-warning-title" className="w-full max-w-lg rounded-[20px] bg-white shadow-[0_24px_70px_rgba(0,7,79,0.25)]">
            <div className="border-b border-border px-6 py-5 sm:px-8"><h2 id="single-resume-warning-title" className="text-xl font-semibold text-primary">Warning</h2></div>
            <div className="px-6 py-8 text-base text-[#404040] sm:px-8"><p>In order to replace your resume, please upload new file first</p></div>
            <div className="flex justify-end border-t border-border px-6 py-4 sm:px-8"><Button type="button" variant="destructive" onClick={() => setShowSingleResumeWarning(false)}>Close</Button></div>
          </div>
        </div>
      ) : null}
    </>
  )
}

/**
 * Real preview, client-side — legacy generated `htmlFileName` via CloudConvert (a paid API this
 * environment has no credential for, see app.module.ts); PDFs render natively in an `<iframe>`
 * (browsers have handled that for years), DOCX via `docx-preview` (MIT, no server round-trip).
 * Fetched as an authenticated blob, not a plain `<iframe src="/api/...">` — an iframe's own
 * request carries no bearer token.
 */
function ResumePreviewModal({ cv, index, onClose }: { cv: TalentProfile['cvs'][number]; index: number; onClose(): void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [kind, setKind] = useState<'pdf' | 'docx' | 'other' | null>(null)
  const [error, setError] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    const ext = cv.fileName?.split('.').pop()?.toLowerCase()

    getResumeFile(index)
      .then(async (blob) => {
        if (cancelled) return
        if (ext === 'docx') {
          setKind('docx')
          const { renderAsync } = await import('docx-preview')
          if (cancelled || !containerRef.current) return
          await renderAsync(blob, containerRef.current, undefined, { className: 'docx-preview', ignoreWidth: true })
          return
        }
        url = URL.createObjectURL(blob)
        setBlobUrl(url)
        setKind(ext === 'pdf' ? 'pdf' : 'other')
      })
      .catch(() => setError('Could not load this resume.'))

    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [index, cv.fileName])

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-primary/45 p-4 backdrop-blur-[2px]" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-labelledby="resume-preview-title" className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_24px_70px_rgba(0,7,79,0.25)]">
        <div className="flex items-center justify-between border-b border-border px-6 py-5">
          <h2 id="resume-preview-title" className="text-xl font-semibold text-primary">{cv.displayName ?? cv.fileName}</h2>
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
        </div>
        <div className="min-h-[420px] flex-1 overflow-auto bg-[#f5f5f5] p-4">
          {error ? <p role="alert" className="p-8 text-center text-sm text-destructive">{error}</p> : null}
          {!error && !kind ? <p className="p-8 text-center text-sm text-muted-foreground">Loading preview…</p> : null}
          {kind === 'pdf' && blobUrl ? <iframe src={blobUrl} title="Resume preview" className="h-[65vh] w-full rounded-lg border border-border bg-white" /> : null}
          {kind === 'docx' ? <div ref={containerRef} className="mx-auto max-w-full overflow-auto rounded-lg bg-white p-4 shadow-sm" /> : null}
          {kind === 'other' && blobUrl ? (
            <div className="flex flex-col items-center gap-4 p-8 text-center">
              <FileText className="size-16 text-accent" />
              <p className="text-sm text-muted-foreground">Preview not available for this file type.</p>
              <a href={blobUrl} download={cv.displayName ?? cv.fileName} className="inline-flex"><Button type="button">Download</Button></a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function BlockedCompanies({ onAdd, exclusions, onRemoveExclusion, suggestions = [], onBlockSuggested }: { onAdd(): void; exclusions: Exclusion[]; onRemoveExclusion(id: string): void; suggestions?: Array<{ name: string; companyId?: string; logoUrl?: string }>; onBlockSuggested?(suggestion: { name: string; companyId?: string; logoUrl?: string }): void }) {
  const blocked = exclusions.map((e) => ({ id: e._id, name: e.excludedName ?? '', logoUrl: companyLogoUrl(e.excludedLogoUrl) }))
  return (
    <Section title="Blocked companies" action="Add company" onAction={onAdd}>
      <p className="leading-7 text-[#555]">Companies you block will never see your profile or receive it from a recruiter.</p>
      {suggestions.length > 0 && onBlockSuggested ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-3">
          <p className="text-sm text-muted-foreground">From your work history — block with one click:</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <Button key={suggestion.companyId ?? suggestion.name} type="button" size="sm" variant="outline" onClick={() => onBlockSuggested(suggestion)}>
                Block {suggestion.name}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
      {blocked.map((company) => <div key={company.id} className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3"><div className="flex items-center gap-3"><div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#f1f4ff] text-primary">{company.logoUrl ? <img src={company.logoUrl} alt="" className="size-full object-contain" /> : <BriefcaseBusiness className="size-4" />}</div><strong className="text-[#404040]">{company.name}</strong></div><Button size="sm" variant="ghost" onClick={() => onRemoveExclusion(company.id)}>Unblock</Button></div>)}
      {blocked.length === 0 ? <EmptyState text="You haven't blocked any companies." /> : null}
    </Section>
  )
}

function Section({ title, action, onAction, children }: { title: string; action: string; onAction?: () => void; children: React.ReactNode }) {
  return (
    <Card className="shadow-[0_2px_10px_rgba(0,0,0,0.035)]">
      <CardHeader className="flex-row items-center justify-between border-b border-border pb-4">
        <CardTitle>{title}</CardTitle>
        <Button type="button" variant="ghost" size="sm" onClick={onAction}><Plus className="size-4" /> {action}</Button>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">{children}</CardContent>
    </Card>
  )
}

function TimelineItem({ icon: Icon, logoUrl, title, subtitle, detail, onEdit, onRemove }: { icon: typeof BriefcaseBusiness; logoUrl?: string; title: string; subtitle: string; detail?: string; onEdit(): void; onRemove(): void }) {
  return <div className="flex gap-4"><div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#f1f4ff] text-primary">{logoUrl ? <img src={logoUrl} alt="" className="size-full object-contain" /> : <Icon className="size-5" />}</div><div className="min-w-0 flex-1"><strong className="text-[#404040]">{title}</strong><p className="mt-1 text-sm text-[#555]">{subtitle}</p>{detail ? <p className="mt-1 text-sm text-muted-foreground">{detail}</p> : null}</div><Button type="button" size="icon" variant="ghost" aria-label={`Edit ${title}`} onClick={onEdit}><Pencil className="size-4" /></Button><Button type="button" size="icon" variant="ghost" aria-label={`Remove ${title}`} onClick={onRemove}><Trash2 className="size-4" /></Button></div>
}

function Preference({ label, value }: { label: string; value: string }) {
  return <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-2 font-medium text-[#404040]">{value}</p></div>
}

function PreferenceEditSection({ title, editing, onEdit, onSave, onCancel, children }: { title: string; editing: boolean; onEdit(): void; onSave(): void; onCancel(): void; children: React.ReactNode }) {
  return <Card className="shadow-[0_2px_10px_rgba(0,0,0,0.035)]"><CardHeader className="flex-row items-center justify-between border-b border-border pb-4"><CardTitle>{title}</CardTitle>{editing ? <div className="flex gap-2"><Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button><Button type="button" size="sm" onClick={onSave}>Save</Button></div> : <Button type="button" size="sm" variant="ghost" onClick={onEdit}><Pencil className="size-4" /> Edit</Button>}</CardHeader><CardContent className="pt-5">{children}</CardContent></Card>
}

function PreferenceTags({ label, values }: { label: string; values: string[] }) {
  return <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><div className="mt-2 flex flex-wrap gap-2">{values.length ? values.map((value) => <Badge key={value} variant="outline">{value}</Badge>) : <span className="text-sm text-muted-foreground">Not selected</span>}</div></div>
}

function EditorFields({
  editor,
  draft,
  setDraft,
  dateFieldType = 'text',
  degreeTypeOptions,
  degreeNameOptions,
  languageOptions,
  languageLevelOptions,
  roleOptions,
  onLocationSearch,
  onLocationPick,
  onCompanySearch,
}: {
  editor: Exclude<Editor, null>
  draft: Record<string, string>
  setDraft(value: Record<string, string> | ((current: Record<string, string>) => Record<string, string>)): void
  dateFieldType?: 'date' | 'text'
  degreeTypeOptions: SearchPickerOption[]
  degreeNameOptions: SearchPickerOption[]
  languageOptions: SearchPickerOption[]
  languageLevelOptions: SearchPickerOption[]
  roleOptions: SearchPickerOption[]
  onLocationSearch(query: string): Promise<SearchPickerOption[]>
  onLocationPick(option: SearchPickerOption): void
  onCompanySearch(query: string): Promise<SearchPickerOption[]>
}) {
  // Functional form — a `SearchPicker` fires `onPick` then `onChange` synchronously from the same
  // click, so two `setDraft` calls land in the same React batch. A plain `{ ...draft, [key]: value }`
  // closes over the PRE-click `draft` and, applied second, clobbers whatever the first call just
  // added (e.g. `onPick`'s `titleId`) — the exact bug that silently dropped ids here before this
  // comment existed. `(current) => ...` reads each update against the PRIOR update's result instead.
  const set = (key: string, value: string) => setDraft((current) => ({ ...current, [key]: value }))
  if (editor === 'about') return <DialogField label="About me"><textarea value={draft.value ?? ''} onChange={(event) => set('value', event.target.value)} rows={5} placeholder="Description" className="rounded-xl border border-input-border bg-input px-[15px] py-3 text-sm font-normal outline-none transition duration-200 placeholder:text-base placeholder:text-[#c7c7c7] focus:border-accent focus:placeholder:text-transparent" /></DialogField>
  if (editor === 'languages') {
    {
      return <div className="grid gap-5 sm:grid-cols-2">
        <DialogField label="Language"><SearchPicker multiple={false} options={languageOptions} values={draft.language ? [draft.language] : []} onChange={(next) => set('language', next[0] ?? '')} onPick={(option) => setDraft((current) => ({ ...current, language: option.label, languageId: option.id }))} placeholder="Search a language…" /></DialogField>
        <DialogField label="Proficiency Level"><SearchPicker multiple={false} options={languageLevelOptions} values={draft.level ? [draft.level] : []} onChange={(next) => set('level', next[0] ?? '')} onPick={(option) => setDraft((current) => ({ ...current, level: option.label, levelId: option.id }))} placeholder="Select a level…" /></DialogField>
      </div>
    }
    return <div className="grid gap-5 sm:grid-cols-2"><DialogField label="Add Language"><ProfileInput value={draft.language ?? ''} onChange={(value) => set('language', value)} placeholder="Choose a language" required /></DialogField><DialogField label="Proficiency Level"><ProfileInput value={draft.level ?? ''} onChange={(value) => set('level', value)} placeholder="Choose level" required /></DialogField></div>
  }
  if (editor === 'blocked') return <DialogField label="Company name">
    <SearchPicker
      multiple={false}
      values={draft.value ? [draft.value] : []}
      onChange={(next) => set('value', next[0] ?? '')}
      onSearch={onCompanySearch}
      onQueryChange={(q) => set('value', q)}
      onPick={(option) => {
        const company = option.data as CompanyItem | undefined
        setDraft((current) => ({ ...current, value: option.label, companyId: option.id, ...(company?.logoUrl && { companyLogoUrl: company.logoUrl }) }))
      }}
      placeholder="Search a company to block…"
    />
  </DialogField>
  if (editor === 'social') return <div className="grid gap-5">{socialLinkMeta.map(({ key, label, icon: Icon }) => <DialogField key={key} label={label}><div className="relative"><Icon className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><input type="url" maxLength={1000} value={draft[key] ?? ''} onChange={(event) => set(key, event.target.value)} placeholder={`${label} link`} className="min-h-11 w-full rounded-xl border border-input-border bg-input py-2.5 pl-11 pr-11 text-base font-normal outline-none transition duration-200 placeholder:text-[#c7c7c7] focus:border-accent focus:placeholder:text-transparent" />{draft[key] ? <button type="button" onClick={() => set(key, '')} aria-label={`Clear ${label}`} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive">×</button> : null}</div></DialogField>)}</div>
  if (editor === 'experience') {
    const isCurrent = draft.current === 'true'
    return <div className="grid gap-5 sm:grid-cols-2">
      <DialogField label="Company">
        <SearchPicker
            multiple={false}
            values={draft.company ? [draft.company] : []}
            onChange={(next) => set('company', next[0] ?? '')}
            onSearch={onCompanySearch}
            onPick={(option) => setDraft((current) => ({ ...current, company: option.label, companyId: option.id }))}
            placeholder="Search a company…"
          />

      </DialogField>
      <DialogField label="Position">
        <SearchPicker
            multiple={false}
            options={roleOptions}
            values={draft.title ? [draft.title] : []}
            onChange={(next) => set('title', next[0] ?? '')}
            onPick={(option) => setDraft((current) => ({ ...current, title: option.label, titleId: option.id }))}
            placeholder="Search a position…"
          />

      </DialogField>
      <DialogField label="Start date:"><ProfileInput type={dateFieldType} value={draft.startDate ?? ''} onChange={(value) => set('startDate', value)} placeholder="Set start date" required /></DialogField>
      {!isCurrent ? <DialogField label="End date:"><ProfileInput type={dateFieldType} value={draft.endDate ?? ''} onChange={(value) => set('endDate', value)} placeholder="Set end date" required /></DialogField> : null}
      <label className="flex min-h-11 cursor-pointer items-center gap-3 self-end text-base font-normal text-[#404040]">
        <input
          type="checkbox"
          checked={isCurrent}
          onChange={(event) => setDraft({ ...draft, current: String(event.target.checked), ...(event.target.checked ? { endDate: '' } : {}) })}
          className="size-5 rounded border-2 border-input-border accent-accent"
        />
        Current
      </label>
      <DialogField label="Job experience Description"><textarea value={draft.description ?? ''} onChange={(event) => set('description', event.target.value)} rows={3} placeholder="Description" className="rounded-xl border border-input-border bg-input px-[15px] py-3 text-sm font-normal outline-none transition duration-200 placeholder:text-base placeholder:text-[#c7c7c7] focus:border-accent focus:placeholder:text-transparent" /></DialogField>
    </div>
  }
  if (editor === 'education') {
    return <div className="grid gap-5 sm:grid-cols-2">
      <DialogField label="Degree type"><SearchPicker multiple={false} options={degreeTypeOptions} values={draft.degreeType ? [draft.degreeType] : []} onChange={(next) => set('degreeType', next[0] ?? '')} onPick={(option) => setDraft((current) => ({ ...current, degreeType: option.label, degreeTypeId: option.id }))} placeholder="Search a degree type…" /></DialogField>
      <DialogField label="Field"><SearchPicker multiple={false} options={degreeNameOptions} values={draft.degree ? [draft.degree] : []} onChange={(next) => set('degree', next[0] ?? '')} onPick={(option) => setDraft((current) => ({ ...current, degree: option.label, degreeNameId: option.id }))} placeholder="Search a field of study…" /></DialogField>
      <DialogField label="School">
        <SearchPicker
          multiple={false}
          values={draft.institution ? [draft.institution] : []}
          onChange={(next) => set('institution', next[0] ?? '')}
          onSearch={onCompanySearch}
          onQueryChange={(q) => set('institution', q)}
          onPick={(option) => setDraft((current) => ({ ...current, institution: option.label, institutionId: option.id }))}
          placeholder="Search a school…"
        />
      </DialogField>
      <DialogField label="Graduation date:"><ProfileInput type={dateFieldType} value={draft.graduationDate ?? ''} onChange={(value) => set('graduationDate', value)} placeholder="Set graduation date" required /></DialogField>
    </div>
  }
  if (editor === 'personal') {
    return <div className="grid gap-5 sm:grid-cols-2">
      <DialogField label="First Name"><ProfileInput value={draft.firstName ?? ''} onChange={(value) => set('firstName', value)} placeholder="Enter first name" required /></DialogField>
      <DialogField label="Last Name"><ProfileInput value={draft.lastName ?? ''} onChange={(value) => set('lastName', value)} placeholder="Enter last name" required /></DialogField>
      <DialogField label="Headline"><ProfileInput value={draft.headline ?? ''} onChange={(value) => set('headline', value)} placeholder="Enter headline" /></DialogField>
      <DialogField label="Location"><SearchPicker multiple={false} values={draft.location ? [draft.location] : []} onChange={(next) => set('location', next[0] ?? '')} onSearch={onLocationSearch} onPick={onLocationPick} placeholder="Search a city…" /></DialogField>
    </div>
  }
  // Every editor above returns. Reaching here means a section was added without a form, and a
  // silent empty dialog is worse than an obvious gap.
  return null
}

function ProfileInput({ value, onChange, placeholder, required = false, type = 'text' }: { value: string; onChange(value: string): void; placeholder: string; required?: boolean; type?: string }) {
  return <input type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} required={required} className="min-h-11 rounded-xl border border-input-border bg-input px-[15px] py-2.5 text-base font-normal outline-none transition duration-200 placeholder:text-[#c7c7c7] focus:border-accent focus:placeholder:text-transparent" />
}

function editorTitle(editor: Exclude<Editor, null>, editing = false) {
  return ({ personal: 'Edit personal info', social: 'Edit Social Networks', about: 'Edit summary', skills: 'Edit skills', experience: editing ? 'Edit Job' : 'Add Job', education: editing ? 'Edit Education' : 'Add Education', languages: editing ? 'Edit language' : 'Add language', blocked: 'Block a company' } as const)[editor]
}

function EmptyState({ text }: { text: string }) {
  return <div className="mt-4 rounded-xl border border-dashed border-[#c7c7c7] p-8 text-center text-sm text-muted-foreground">{text}</div>
}
